import React from 'react';

import {
  CaraWorkspaceArtifactType,
  type CaraWorkspaceFieldsFragment,
  type CaraWorkspaceMessageFollowUpInput,
  CaraWorkspaceState
} from '~/api/graphql';
import type { CaraAttachment } from '~/components/cara-input/cara-attachment';
import { buildTemplateTitleFromDocumentTitle } from '~/components/workspaces/lib/utils';

type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

type AddCaraWorkspaceMessage = (
  query: string,
  attachments: CaraAttachment[],
  followUp?: CaraWorkspaceMessageFollowUpInput
) => Promise<void>;

/**
 * Owns the entire "convert document to a reusable template" lifecycle:
 *
 *  1. Caller invokes `startConvertFromArtifactId(artifact)`.
 *  2. Hook builds the template-title string and dispatches a "Convert ... to a reusable
 *     template" chat message via the supplied `addCaraWorkspaceMessage`.
 *  3. Hook watches for a new DocumentHtml artifact whose `sourceFileId` matches the
 *     pending source artifact id; when it appears, focuses it and signals the
 *     SaveAsTemplateButton to auto-open via `autoOpenSaveDialog`.
 *  4. If the workspace stops working without producing a converted artifact, the safety
 *     net effect clears the pending state so the progress banner doesn't get stuck.
 *
 * URL-driven kickoff is gated by `markUrlConvertProcessed(key)` which dedupes across
 * renders so the URL effect only fires once per `?artifact_id=&version=` pair.
 */
export function useConvertDocumentToTemplate(args: {
  workspace: CaraWorkspaceFieldsFragment | undefined;
  addCaraWorkspaceMessage: AddCaraWorkspaceMessage;
  setCurrentArtifact: (artifact: Artifact) => void;
}) {
  const { workspace, addCaraWorkspaceMessage, setCurrentArtifact } = args;

  const [isConvertingToTemplate, setIsConvertingToTemplate] = React.useState(false);
  const [autoOpenSaveDialog, setAutoOpenSaveDialog] = React.useState(false);

  const pendingConvertSourceIdRef = React.useRef<string | null>(null);
  // Pending message to send once we know the workspace is ready (we may receive the
  // start request before the first subscription tick has populated the workspace).
  const pendingConvertMessageRef = React.useRef<{ templateTitle: string } | null>(null);
  // Dedupes URL-driven conversion across re-renders. Keyed by `${artifactId}-${version}`.
  const urlConvertProcessedRef = React.useRef<string | null>(null);

  const startConvertFromArtifactId = React.useCallback(
    (artifact: Artifact) => {
      const templateTitle = buildTemplateTitleFromDocumentTitle(artifact.document?.title);
      pendingConvertSourceIdRef.current = artifact.id;
      setIsConvertingToTemplate(true);

      if (!workspace?.id) {
        // Defer until the workspace is hydrated; the effect below will dispatch.
        pendingConvertMessageRef.current = { templateTitle };
        return;
      }
      addCaraWorkspaceMessage(`Convert "${templateTitle}" to a reusable template`, []);
    },
    [workspace?.id, addCaraWorkspaceMessage]
  );

  /**
   * Returns true if the URL-driven convert has not yet been processed for this
   * artifact+version pair. Marks it as processed so subsequent re-renders skip.
   */
  const markUrlConvertProcessed = React.useCallback((key: string): boolean => {
    if (urlConvertProcessedRef.current === key) return false;
    urlConvertProcessedRef.current = key;
    return true;
  }, []);

  // If startConvert was called before the workspace was hydrated, dispatch the message
  // as soon as the workspace becomes available.
  React.useEffect(() => {
    if (pendingConvertMessageRef.current && workspace?.id) {
      const { templateTitle } = pendingConvertMessageRef.current;
      pendingConvertMessageRef.current = null;
      addCaraWorkspaceMessage(`Convert "${templateTitle}" to a reusable template`, []);
    }
  }, [workspace?.id, addCaraWorkspaceMessage]);

  // When Cara emits the converted-from artifact (matched via sourceFileId), focus it and
  // auto-open the save-as-template dialog so the user can finish naming/saving without
  // re-clicking.
  React.useEffect(() => {
    if (!pendingConvertSourceIdRef.current || !workspace?.artifacts) return;
    const converted = workspace.artifacts.find(
      (a) =>
        a.type === CaraWorkspaceArtifactType.DocumentHtml &&
        a.document?.sourceFileId === pendingConvertSourceIdRef.current
    );
    if (converted) {
      pendingConvertSourceIdRef.current = null;
      setIsConvertingToTemplate(false);
      setCurrentArtifact(converted);
      setAutoOpenSaveDialog(true);
    }
  }, [workspace?.updatedAt, workspace?.artifacts, setCurrentArtifact]);

  // Safety net: if the workspace stops working without producing a converted artifact,
  // clear the pending state so the progress banner doesn't get stuck.
  React.useEffect(() => {
    if (workspace?.state !== CaraWorkspaceState.Working && pendingConvertSourceIdRef.current) {
      pendingConvertSourceIdRef.current = null;
      setIsConvertingToTemplate(false);
    }
  }, [workspace?.state]);

  return {
    isConvertingToTemplate,
    autoOpenSaveDialog,
    setAutoOpenSaveDialog,
    startConvertFromArtifactId,
    markUrlConvertProcessed
  };
}
